import React from 'react'
import { Box, Divider, Grid, Typography } from "@mui/material"
import { ModalSeeker, AutocompleteAjaxSeeker, ButtonLoadingSeeker, IconSeeker, FlagsIconSeeker } from "@ui-seeker/core"
import { translateCountryName } from "@ui-seeker/utils"
import { useSignal } from "@preact/signals-react"
import { postSubmit } from "@ui-seeker/utils"
import { BACKEND_APIS } from "@constants"
import { AppAsyncOfficesAutocomplete, AppSitesAutocomplete, AppProvidersAutocomplete } from "@components"
import { bulkFieldModal, acrissTable, categoriesTable } from "Pages/User/CustomMapping/@signals"
import { MAPPING_TYPE } from "Pages/User/CustomMapping/@constants"
import { useToast } from "@hooks"

const FIELD_LABELS = {
	countries: 'Countries',
	offices: 'Offices',
	sites: 'Sites',
	providers: 'Providers',
}

export default function BulkFieldModal({ path }) {
	const modal = bulkFieldModal.value
	const isOpen = !!modal?.field && modal?.path === path
	const field = modal?.field
	const rows = modal?.rows || []
	const values = useSignal([])
	const isLoading = useSignal(false)
	const { showToast } = useToast()

	const affectedRows = field === 'countries'
		? rows.filter(r => !r.idOffices?.length)
		: field === 'offices'
		? rows.filter(r => !r.idCountries?.length)
		: rows

	const skippedCount = (field === 'countries' || field === 'offices') ? rows.length - affectedRows.length : 0

	const handleClose = () => {
		bulkFieldModal.value = null
		values.value = []
	}

	const handleAction = async (action) => {
		if (!values.value?.length || !affectedRows.length) return

		const api = path === MAPPING_TYPE.ACRISS ? BACKEND_APIS.CUSTOM_ACRISS : BACKEND_APIS.CUSTOM_CATEGORY
		const idCustom = affectedRows.map(r => r.idCustom)

		isLoading.value = true
		try {
			const updated = await postSubmit(`${api}/bulk-update`, { idCustom, field, action, values: values.value })

			const tableSignal = path === MAPPING_TYPE.ACRISS ? acrissTable : categoriesTable
			const updatedMap = Object.fromEntries(updated.map(r => [r.idCustom, r]))
			tableSignal.value = tableSignal.value.map(r => updatedMap[r.idCustom] || r)

			handleClose()
		} catch {
			showToast.error('Error updating mappings')
		} finally {
			isLoading.value = false
		}
	}

	return <ModalSeeker
		isOpen={isOpen}
		controller={handleClose}
		hideActions
		containerProps={{ maxWidth: "sm" }}
	>
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Typography variant="h5">
					Edit {FIELD_LABELS[field]} — {affectedRows.length} mapping{affectedRows.length !== 1 ? 's' : ''}
					{skippedCount > 0 && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
						({skippedCount} skipped — have specific offices)
					</Typography>}
				</Typography>
			</Grid>

			<Grid item xs={12}>
				{field === 'countries' && <AutocompleteAjaxSeeker
					multiple
					filterSelectedOptions={false}
					label="Countries"
					placeholder="Select countries..."
					urlAjax={`${BACKEND_APIS.MASTER_DATA}/options/office-countries`}
					value={values.value}
					onChange={e => { values.value = e.target.value }}
					formatOptionLabel={({ value }) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<FlagsIconSeeker countryName={value}/>
						<Typography>{translateCountryName(value)}</Typography>
					</Box>}
				/>}
				{field === 'offices' && <AppAsyncOfficesAutocomplete
					multiple
					value={values.value}
					onChange={e => { values.value = e.target.value }}
				/>}
				{field === 'sites' && <AppSitesAutocomplete
					value={values.value}
					onChange={e => { values.value = e.target.value }}
				/>}
				{field === 'providers' && <AppProvidersAutocomplete
					value={values.value}
					onChange={e => { values.value = e.target.value }}
				/>}
			</Grid>

			<Grid item xs={12}>
				<Divider/>
			</Grid>

			<Grid item xs={6} sx={{ display: 'flex', justifyContent: 'center' }}>
				<ButtonLoadingSeeker
					disabled={!values.value?.length || !affectedRows.length}
					loading={isLoading.value}
					onClick={() => handleAction('remove')}
					startIcon={<IconSeeker icon="trash-can"/>}
					sx={{ backgroundColor: 'colors.red', color: 'colors.white', minWidth: 120 }}
				>
					Remove
				</ButtonLoadingSeeker>
			</Grid>
			<Grid item xs={6} sx={{ display: 'flex', justifyContent: 'center' }}>
				<ButtonLoadingSeeker
					disabled={!values.value?.length || !affectedRows.length}
					loading={isLoading.value}
					onClick={() => handleAction('add')}
					startIcon={<IconSeeker icon="plus"/>}
					sx={{ backgroundColor: 'colors.green', color: 'colors.white', minWidth: 120 }}
				>
					Add
				</ButtonLoadingSeeker>
			</Grid>
		</Grid>
	</ModalSeeker>
}
